import React from "react";
import { motion } from "framer-motion";
import { ClipboardList, Cpu, ShieldCheck, X, Zap, ArrowDown } from "lucide-react";
import { RevealOnScroll } from "../shared";
import {
  ECOSYSTEM_MANIFEST,
  BOT_FAILURE_TRIGGERS,
  BUILD_CREW,
} from "./comparisonContent";

const CREW_ICONS = {
  ba: ClipboardList,
  eng: Cpu,
  qa: ShieldCheck,
};

const FAILURE_CODES = ["SIG-01", "SIG-02", "SIG-03"];

const CornerBracket = ({ position }) => {
  const positions = {
    tl: "top-3 left-3",
    tr: "top-3 right-3",
    bl: "bottom-3 left-3",
    br: "bottom-3 right-3",
  };
  const isRight = position === "tr" || position === "br";
  const isBottom = position === "bl" || position === "br";

  return (
    <span className={`absolute w-5 h-5 pointer-events-none ${positions[position]}`} aria-hidden="true">
      <span
        className={`absolute ${isBottom ? "bottom-0" : "top-0"} ${isRight ? "right-0" : "left-0"} w-full h-px bg-[#00D2FF]/20`}
      />
      <span
        className={`absolute ${isBottom ? "bottom-0" : "top-0"} ${isRight ? "right-0" : "left-0"} w-px h-full bg-[#00D2FF]/20`}
      />
    </span>
  );
};

const FailureCard = ({ trigger, index }) => (
  <motion.li
    initial={{ opacity: 0, x: -16 }}
    whileInView={{ opacity: 1, x: 0 }}
    viewport={{ once: true, margin: "-30px" }}
    transition={{ duration: 0.45, delay: index * 0.1 }}
    className="relative rounded-lg border border-[#2A3348] bg-[#131820] overflow-hidden group hover:border-red-500/20 transition-colors"
  >
    <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-gradient-to-b from-red-500/40 via-red-500/20 to-transparent" />
    <div className="p-4 pl-5">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg border border-red-500/20 bg-red-500/5 flex items-center justify-center flex-shrink-0 group-hover:bg-red-500/10 transition-colors">
          <X className="w-4 h-4 text-red-400/80" />
        </div>
        <div className="min-w-0">
          <p className="font-mono text-[10px] text-red-400/55 tracking-wider mb-1">
            {FAILURE_CODES[index]}
          </p>
          <p className="font-semibold text-[#B8C5D9] leading-snug">{trigger.title}</p>
          <p className="text-sm text-[#9AA8BE] font-body leading-relaxed mt-1.5">{trigger.detail}</p>
        </div>
      </div>
    </div>
  </motion.li>
);

const CrewTimelineStep = ({ member, index, isLast }) => {
  const Icon = CREW_ICONS[member.id] ?? Cpu;

  return (
    <motion.div
      initial={{ opacity: 0, x: 16 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: "-30px" }}
      transition={{ duration: 0.45, delay: index * 0.12 }}
      className="relative flex gap-4"
      data-testid={`build-crew-step-${index}`}
    >
      <div className="flex flex-col items-center flex-shrink-0">
        <div className="w-11 h-11 rounded-xl border border-[#00D2FF]/25 bg-[#00D2FF]/6 flex items-center justify-center">
          <Icon className="w-4 h-4 text-partner-accent" />
        </div>
        {!isLast && (
          <div className="w-px flex-1 min-h-[2rem] mt-2 bg-gradient-to-b from-[#00D2FF]/25 via-[#2A3348] to-[#2A3348]" />
        )}
      </div>

      <div className={`flex-1 min-w-0 ${isLast ? "pb-0" : "pb-6"}`}>
        <div className="rounded-lg border border-[#2A3348] bg-[#131820] p-4 sm:p-5 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-24 h-24 bg-[#00D2FF]/3 rounded-full blur-2xl pointer-events-none" />
          <div className="relative flex items-start justify-between gap-3 mb-2">
            <div>
              <p className="font-mono text-[10px] text-partner-accent font-semibold tracking-wider mb-1">
                PHASE {String(index + 1).padStart(2, "0")}
              </p>
              <h4 className="text-base font-semibold text-partner-accent leading-snug">{member.role}</h4>
            </div>
            <div className="text-right flex-shrink-0">
              <span className="font-mono text-xl sm:text-2xl font-semibold text-partner-accent tabular-nums leading-none block">
                {member.stat}
              </span>
              <p className="text-xs sm:text-sm text-partner-accent font-body leading-snug mt-1">
                {member.statLabel}
              </p>
            </div>
          </div>
          <p className="relative text-sm text-partner/75 font-body leading-[1.75]">{member.description}</p>
        </div>
      </div>
    </motion.div>
  );
};

const VsDivider = ({ vertical = true }) => (
  <div
    className={
      vertical
        ? "hidden lg:flex flex-col items-center justify-center px-4 py-8 relative"
        : "flex lg:hidden items-center pt-5 -mx-1"
    }
  >
    {vertical ? (
      <>
        <div className="absolute inset-y-4 left-1/2 -translate-x-1/2 w-px bg-gradient-to-b from-transparent via-[#2A3348] to-transparent" />
        <div className="relative z-10 w-11 h-11 rounded-full border border-[#2A3348] bg-[#131820] flex items-center justify-center">
          <span className="font-mono text-[10px] font-semibold text-[#9AA8BE] tracking-widest">VS</span>
        </div>
      </>
    ) : (
      <>
        <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#2A3348] to-transparent" />
        <div className="mx-4 w-10 h-10 rounded-full border border-[#2A3348] bg-[#131820] flex items-center justify-center flex-shrink-0">
          <ArrowDown className="w-4 h-4 text-[#9AA8BE]" />
        </div>
        <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#2A3348] to-transparent" />
      </>
    )}
  </div>
);

const EcosystemManifestPanel = () => (
  <RevealOnScroll>
    <div
      className="relative rounded-2xl border border-[#2A3348] overflow-hidden bg-[#11151F]"
      data-testid="ecosystem-manifest"
    >
      <div className="absolute inset-0 mesh-gradient pointer-events-none opacity-25" />
      <div
        className="absolute inset-0 opacity-[0.015] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(#00D2FF 1px, transparent 1px), linear-gradient(90deg, #00D2FF 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }}
      />

      <CornerBracket position="tl" />
      <CornerBracket position="tr" />
      <CornerBracket position="bl" />
      <CornerBracket position="br" />

      <div className="relative border-b border-[#2A3348] px-5 sm:px-8 py-6 sm:py-8">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <span className="badge-mono text-[#9AA8BE]">{ECOSYSTEM_MANIFEST.tag}</span>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-[#00D2FF]/20 bg-[#00D2FF]/5">
            <Zap className="w-3 h-3 text-partner-accent/70 pulse-subtle" />
            <span className="text-[10px] font-mono font-semibold text-partner-accent/70 tracking-wider">
              {ECOSYSTEM_MANIFEST.status}
            </span>
          </span>
        </div>

        <h3 className="text-xl sm:text-2xl md:text-3xl font-semibold text-[#B8C5D9] leading-tight mb-3 max-w-3xl">
          {ECOSYSTEM_MANIFEST.headline.split('"bot"')[0]}
          <span className="gradient-text">&quot;bot&quot;</span>
          {ECOSYSTEM_MANIFEST.headline.split('"bot"')[1]}
        </h3>
        <p className="text-sm sm:text-base text-[#9AA8BE] font-body leading-relaxed max-w-3xl">
          {ECOSYSTEM_MANIFEST.lead}
        </p>

        <div className="flex flex-wrap gap-2 sm:gap-3 mt-5">
          {BUILD_CREW.map((member) => (
            <div
              key={member.id}
              className="px-3 py-1.5 rounded-lg border border-[#2A3348] bg-[#131820] font-mono text-xs"
            >
              <span className="text-partner-accent font-semibold">{member.stat}</span>{" "}
              <span className="text-partner-accent">{member.statLabel}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="relative grid lg:grid-cols-[1fr_auto_1.65fr]">
        <div className="relative p-5 sm:p-8">
          <div className="absolute inset-0 bg-gradient-to-br from-red-950/8 via-transparent to-transparent pointer-events-none" />
          <div className="relative">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-2 h-2 rounded-full bg-red-500/40 pulse-subtle" />
              <p className="text-xs font-semibold text-[#9AA8BE] uppercase tracking-wider">
                {ECOSYSTEM_MANIFEST.botLabel}
              </p>
            </div>
            <p className="text-sm text-[#9AA8BE] font-body leading-relaxed mb-5 max-w-md">
              {ECOSYSTEM_MANIFEST.botLead}
            </p>
            <ul className="space-y-3">
              {BOT_FAILURE_TRIGGERS.map((trigger, index) => (
                <FailureCard key={trigger.id} trigger={trigger} index={index} />
              ))}
            </ul>
            <VsDivider vertical={false} />
          </div>
        </div>

        <VsDivider vertical />

        <div className="relative p-5 sm:p-8 lg:border-l border-[#2A3348]">
          <div className="absolute inset-0 bg-gradient-to-bl from-[#00D2FF]/4 via-transparent to-transparent pointer-events-none" />
          <div className="relative">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-2 h-2 rounded-full bg-[#00D2FF]/50 pulse-subtle" />
              <p className="text-xs font-semibold text-partner-accent/70 uppercase tracking-wider">
                {ECOSYSTEM_MANIFEST.ecosystemLabel}
              </p>
            </div>
            <p className="text-sm text-[#9AA8BE] font-body leading-relaxed mb-6 max-w-xl">
              {ECOSYSTEM_MANIFEST.ecosystemLead}
            </p>
            <div data-testid="build-crew-stats">
              {BUILD_CREW.map((member, index) => (
                <CrewTimelineStep
                  key={member.id}
                  member={member}
                  index={index}
                  isLast={index === BUILD_CREW.length - 1}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  </RevealOnScroll>
);

export default EcosystemManifestPanel;
